import { Snapshot, type LeagueData } from "../schemas.js";
import { href, parseRoute } from "./router.js";
import { emojiFavicon, esc, splitLeadingEmoji } from "./ui.js";
import { homeView } from "./views/home.js";
import { matchupView } from "./views/matchup.js";
import { notFoundView } from "./views/not-found.js";
import { teamView } from "./views/team.js";
import type { View } from "./views/view.js";
import { applyTheme, currentTheme } from "./theme.js";

/**
 * Static-site entry point. Fetches the snapshot, validates it with Zod (the
 * exact same schema the refresh job wrote), then renders the page for the
 * current hash route client-side. No backend involved.
 */

const app = document.getElementById("app")!;
applyTheme(currentTheme());

function snapshotUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="snapshot-url"]');
  const params = new URLSearchParams(location.search);
  return params.get("snapshot") ?? meta?.content ?? "./snapshot.json";
}

function viewFor(league: LeagueData): View {
  const route = parseRoute(location.hash);
  switch (route.name) {
    case "home":
      return homeView(league, route.week);
    case "team":
      return teamView(league, route.teamId);
    case "matchup":
      return matchupView(league, route.week, route.teamId);
    case "notFound":
      return notFoundView();
  }
}

let unmount: (() => void) | void;
function render(league: LeagueData): void {
  unmount?.();
  const view = viewFor(league);
  // The league name's leading emoji becomes the favicon rather than part of the tab title.
  const { emoji, text } = splitLeadingEmoji(league.leagueName);
  document.title = view.title ? `${view.title} · ${text}` : text;
  if (emoji) document.querySelector<HTMLLinkElement>("#favicon")!.href = emojiFavicon(emoji);
  app.innerHTML = `
    <header class="league-head">
      <h1><a href="${href.home()}">${esc(league.leagueName)}</a> <span class="season">${league.season} season</span></h1>
    </header>
    ${view.html}`;
  unmount = view.mount?.(app);
}

async function load(): Promise<void> {
  app.innerHTML = `<p class="loading">Loading standings…</p>`;
  try {
    const res = await fetch(snapshotUrl(), { cache: "no-store" });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    let snap = Snapshot.parse(await res.json()); // Zod validates the payload
    render(snap.league);
    window.addEventListener("hashchange", () => {
      render(snap.league);
      window.scrollTo(0, 0);
    });
    // Refresh open tabs as new snapshots arrive without adding explanatory UI.
    let refreshing = false;
    const refresh = async () => {
      if (document.hidden || refreshing) return;
      refreshing = true;
      try {
        const response = await fetch(snapshotUrl(), { cache: "no-store", signal: AbortSignal.timeout(15_000) });
        if (!response.ok) throw new Error(`Snapshot refresh failed: ${response.status}`);
        const next = Snapshot.parse(await response.json());
        if (next.generatedAt !== snap.generatedAt) {
          snap = next;
          const { scrollX, scrollY } = window;
          render(snap.league);
          window.scrollTo(scrollX, scrollY);
        }
      } catch (error) { console.warn("Snapshot refresh failed", error); }
      finally { refreshing = false; }
    };
    window.setInterval(() => { void refresh(); }, 5 * 60_000);
    document.addEventListener("visibilitychange", () => { void refresh(); });
  } catch (err) {
    app.innerHTML = `<div class="card error"><h2>Couldn't load standings</h2><p>${esc(
      String(err),
    )}</p><p class="hint">Check that the snapshot exists and this page can reach it.</p></div>`;
  }
}

void load();
